'use client';

import type { KeyboardEvent as ReactKeyboardEvent, Ref } from 'react';
import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import clsx from 'clsx';
import type { Key } from '@react-types/shared';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import type { ListLoadingStates, ListProps } from '@frameio/vapor';
import {
  ListBox,
  ListSearch,
  mergeProps,
  useFilteredListState,
  usePrevious,
} from '@frameio/vapor';
import { useInputSelectionOnFocus } from '../../../hooks/useInputSelectionOnFocus';
import {
  useQuickSearchModal,
  useTrackProjectScopeSelected,
  useTrackSearchQueryCleared,
} from '../../../shared/contexts';
import { useQuickSearchModalData } from '../../contexts';
import type { OnSearchSuggestionClick, QuickSearchResults } from '../../types';
import { QuickSearchInitial } from '../QuickSearchInitial';
import {
  ListSkeleton,
  ProjectScopeDropdown,
  ResultsMessage,
} from '../QuickSearchList/components';
import TEXT from '../QuickSearchList/generated/TEXT/QuickSearchList';
import {
  useHasPaneScrolled,
  usePlaceholderText,
} from '../QuickSearchList/hooks';
import { useQuickSearchScrollManager } from '../QuickSearchList/hooks/useQuickSearchScrollManager';
import styles from './QuickSearchCompactList.module.css';
import { useQuickSearchCompactSelectionSync } from './useQuickSearchCompactSelectionSync';

const INITIAL_PANE_ID = 'initial';
const LIST_PANE_ID = 'item-list';

const SUGGESTION_CARDS_PER_SUBSET = 3;
const SUGGESTION_CARD_WIDTH = 226;
const CELL_RESULT_HEIGHT = 106;
const CELL_RESULT_HEIGHT_MOBILE = 88;
const LIST_RESULTS_PADDING_Y = 8;
const LIST_SCROLL_PADDING = {
  top: LIST_RESULTS_PADDING_Y,
  bottom: LIST_RESULTS_PADDING_Y,
};
// Panel max-height (630) minus 8px padding-top minus 2px borders.
const COMPACT_PANEL_INNER_MAX_HEIGHT = 620;
const ADVANCED_SEARCH_ITEM_HEIGHT = 40;
const ADVANCED_AI_SEARCH_ITEM_HEIGHT = 44; // 40 + 4 spacing
// Gap between the AdvancedSearchItem and the first standard result.
const ADVANCED_SEARCH_ITEM_GAP = 8;
// Fixed panel/scroll-area heights for the empty (no name match) state.
const NO_RESULTS_CONTENT_HEIGHT_WITH_AI = 180;
const NO_RESULTS_CONTENT_HEIGHT = 132;
const NO_RESULTS_SCROLL_AREA_HEIGHT_WITH_AI = 56;
const NO_RESULTS_SCROLL_AREA_HEIGHT = 16;

const DefaultListProps = {
  disallowEmptySelection: true,
  itemGap: 0,
};

const ListPropsMobile = {
  selectionMode: 'none' as const,
  estimatedItemHeight: CELL_RESULT_HEIGHT_MOBILE,
};

const ListPropsDesktop = {
  selectionMode: 'single' as const,
  selectionBehavior: 'replace' as const,
  estimatedItemHeight: CELL_RESULT_HEIGHT,
};

export type QuickSearchCompactListProps = Omit<
  ListProps<QuickSearchResult>,
  'items'
> & {
  isMobile: boolean;
  items: QuickSearchResults;
  loading: ListLoadingStates | undefined;
  searchQuery: string;
  focusedKey?: string | null;
  hasAdvancedAiSearchItem?: boolean;
  hasQueriesSettled: boolean;
  isInitial?: boolean;
  onRequestClose: () => void;
  onResetQueryState: () => void;
  onActivateItem?: (
    activeKey?: string,
    event?: React.KeyboardEvent | React.MouseEvent
  ) => void;
  onInputQueryStaleChange?: (isStale: boolean) => void;
  onInputQueryChange?: (query: string) => void;
  initialFocusInputRef?: Ref<HTMLInputElement>;
  resultsListRef?: Ref<HTMLDivElement>;
  NoResults?: React.ReactNode;
};

export function QuickSearchCompactList({
  'aria-label': ariaLabelProp,
  children,
  focusedKey,
  NoResults,
  hasAdvancedAiSearchItem = false,
  hasQueriesSettled,
  initialFocusInputRef,
  isInitial = false,
  isMobile,
  items: itemsProp,
  loading: loadingProp,
  onActivateItem,
  onInputQueryChange,
  onInputQueryStaleChange,
  onRequestClose,
  onRequestSearch,
  onResetQueryState,
  onSelectionChange,
  resultsListRef,
  searchQuery,
  ...otherProps
}: QuickSearchCompactListProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(initialFocusInputRef, () => inputRef.current!, []);

  const trackSearchQueryCleared = useTrackSearchQueryCleared();
  const trackProjectScopeSelected = useTrackProjectScopeSelected();
  const { scheduleSelection } = useInputSelectionOnFocus(inputRef);

  const placeholderText = usePlaceholderText({
    isMobile,
    isSemanticSearchEnabled: false,
  });

  const { addRecentSearch } = useQuickSearchModalData();
  const { clearScopedToProjectId, setScopedToProjectId, scopedToProjectId } =
    useQuickSearchModal();
  const activeProjectId = useActiveProjectId();
  const dropdownRef = useRef<HTMLButtonElement>(null);

  const listProps = mergeProps(
    {
      ...DefaultListProps,
      ...(isMobile ? ListPropsMobile : ListPropsDesktop),
      onRequestSearch,
      loading: loadingProp,
      items: itemsProp,
      onSelectionChange,
      'aria-label': ariaLabelProp,
      children,
      defaultQuery: isInitial ? searchQuery.trim() : undefined,
    },
    otherProps
  );

  const { state: listState } = useFilteredListState(
    listProps as ListProps<QuickSearchResult>
  );

  const hasSearchQuery = listState.queryState.query.trim() !== '';
  const isInputQueryStale =
    listState.queryState.query.trim() !== '' &&
    listState.queryState.query !== searchQuery;

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearchInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        return;
      }

      if (dropdownRef.current) {
        const input = event.currentTarget;
        const isEmpty = input.value === '';
        const cursorAtStart = input.selectionStart === 0;
        if (event.key === 'ArrowLeft' && (isEmpty || cursorAtStart)) {
          event.preventDefault();
          dropdownRef.current.focus();
          return;
        }
      }

      if (event.key === 'Enter' && !event.repeat && onActivateItem) {
        event.preventDefault();
        // Prefer the list's internal focused key so arrow-key navigation
        // is reflected even when selectionMode is 'none'.
        const activeKey =
          listState.selectionManager.focusedKey ?? focusedKey ?? null;
        onActivateItem(String(activeKey), event);
      }
    },
    [onActivateItem, focusedKey, listState.selectionManager]
  );

  const selectListItemFromListKey = useCallback(
    (listKey?: string | number | null) => {
      if (!listKey) return;
      listState.selectionManager.setFocusedKey(listKey);
      listState.selectionManager.replaceSelection(listKey);
      if (
        listKey !== undefined &&
        listKey !== null &&
        String(listKey) !== String(focusedKey ?? '')
      ) {
        onSelectionChange?.(new Set([listKey]) as any);
      }
    },
    [listState.selectionManager, focusedKey, onSelectionChange]
  );

  useQuickSearchCompactSelectionSync({
    collection: listState.collection,
    focusedKey,
    hasAdvancedAiSearchItem,
    hasQueriesSettled,
    hasSearchQuery,
    items: itemsProp as QuickSearchResult[],
    searchQuery,
    selectListItemFromListKey,
  });

  const hasSetInitialSelectedItem = useRef(false);
  const shouldSelectAllOnOpen = useRef<boolean | null>(null);
  const hasAppliedSelection = useRef(false);
  const prevIsInitial = usePrevious(isInitial);

  const handleOnClickSearchSuggestion = useCallback<OnSearchSuggestionClick>(
    (value, highlight) => {
      listState.dispatchQuery({ type: 'query', state: value });
      focusInput();
      if (highlight) {
        hasAppliedSelection.current = true;
        scheduleSelection(highlight);
      }
    },
    [listState, focusInput, scheduleSelection]
  );

  const handleOnClearAll = useCallback(() => {
    onResetQueryState();
    listState.dispatchQuery({ type: 'clear-query' });
    focusInput();
  }, [onResetQueryState, listState, focusInput]);

  const handleOnQueryClear = useCallback(() => {
    inputRef.current?.setSelectionRange(0, 0);
    trackSearchQueryCleared();
  }, [trackSearchQueryCleared]);

  const handleOnProjectPillClear = useCallback(() => {
    if (!scopedToProjectId) {
      focusInput();
      return;
    }
    clearScopedToProjectId();
    trackProjectScopeSelected('AllProjects');
    focusInput();
  }, [
    clearScopedToProjectId,
    focusInput,
    scopedToProjectId,
    trackProjectScopeSelected,
  ]);

  const handleOnSetProjectScope = useCallback(
    (projectId: string) => {
      if (!projectId) return;
      if (scopedToProjectId !== projectId) {
        trackProjectScopeSelected('CurrentProject');
      }
      setScopedToProjectId(projectId);
      focusInput();
    },
    [
      focusInput,
      scopedToProjectId,
      setScopedToProjectId,
      trackProjectScopeSelected,
    ]
  );

  useLayoutEffect(() => {
    onInputQueryStaleChange?.(isInputQueryStale);
  }, [isInputQueryStale, onInputQueryStaleChange]);

  useLayoutEffect(() => {
    onInputQueryChange?.(listState.queryState.query);
  }, [listState.queryState.query, onInputQueryChange]);

  // 'loading-more' keeps the AdvancedSearchItem in the collection so it
  // stays selectable while the skeleton renders below it via renderLoading.
  const listBoxLoadingState: ListLoadingStates | undefined =
    loadingProp === 'loading' || isInputQueryStale
      ? 'loading-more'
      : loadingProp;
  const isShowingLoadingMore = listBoxLoadingState === 'loading-more';

  const renderListBoxLoading = useCallback(() => <ListSkeleton />, []);

  const getFirstListItemKey = useCallback(() => {
    return listState.collection.getFirstKey() ?? null;
  }, [listState.collection]);

  const { handleOnScroll, hasAnyPaneScrolled, resetPaneScrollState } =
    useHasPaneScrolled({ paneIds: [LIST_PANE_ID, INITIAL_PANE_ID] });

  const { handleOnListScroll, handleOnInitialScroll } =
    useQuickSearchScrollManager({
      listState,
      hasCollectionSettled: true,
      handleOnScroll,
      hasAnyPaneScrolled,
      resetPaneScrollState,
      addRecentSearch,
      selectListItemFromListKey,
      getFirstListItemKey,
      focusInput,
      paneIds: {
        list: LIST_PANE_ID,
        detail: LIST_PANE_ID,
        initial: INITIAL_PANE_ID,
      },
      isInitial,
    });

  useLayoutEffect(() => {
    if (
      isInitial &&
      !hasSetInitialSelectedItem.current &&
      listState.collection.size > 0 &&
      hasSearchQuery
    ) {
      selectListItemFromListKey(focusedKey);
      hasSetInitialSelectedItem.current = true;
    }
  }, [
    isInitial,
    listState.collection.size,
    hasSearchQuery,
    selectListItemFromListKey,
    focusedKey,
  ]);

  useLayoutEffect(() => {
    const isFirstMount = shouldSelectAllOnOpen.current === null;
    const isModalReopen = isInitial && prevIsInitial === false;
    if (isFirstMount || isModalReopen) {
      shouldSelectAllOnOpen.current = searchQuery.trim() !== '';
      hasAppliedSelection.current = false;
    }
    if (
      isInitial &&
      hasSearchQuery &&
      shouldSelectAllOnOpen.current &&
      !hasAppliedSelection.current
    ) {
      hasAppliedSelection.current = true;
      scheduleSelection({ starts: 0, ends: Infinity });
    }
  }, [
    isInitial,
    prevIsInitial,
    hasSearchQuery,
    searchQuery,
    scheduleSelection,
  ]);

  const scrollToKey = useMemo(() => {
    return isInitial && focusedKey
      ? { id: focusedKey as Key, scroll: 'smooth' as const }
      : undefined;
  }, [focusedKey, isInitial]);

  const hasLoadingError = loadingProp === 'loading-error';
  const shouldShowProjectPill = activeProjectId != null;

  // Size the results container to fit content, capped at the panel's inner
  // max so the ListBox scrolls when there are many items. An inline definite
  // height is required for the ListBox's `height: 100%` scrollView to resolve.
  const resultsContentHeight = useMemo(() => {
    if (isShowingLoadingMore) {
      return COMPACT_PANEL_INNER_MAX_HEIGHT;
    }
    const itemHeight = isMobile
      ? CELL_RESULT_HEIGHT_MOBILE
      : CELL_RESULT_HEIGHT;
    const itemsBlockHeight =
      itemsProp.length > 0
        ? ADVANCED_SEARCH_ITEM_GAP + itemsProp.length * itemHeight
        : 0;
    const intrinsicHeight =
      ADVANCED_SEARCH_ITEM_HEIGHT +
      (hasAdvancedAiSearchItem ? ADVANCED_AI_SEARCH_ITEM_HEIGHT : 0) +
      itemsBlockHeight +
      LIST_RESULTS_PADDING_Y * 2;

    return (
      (NoResults &&
        (hasAdvancedAiSearchItem
          ? NO_RESULTS_CONTENT_HEIGHT_WITH_AI
          : NO_RESULTS_CONTENT_HEIGHT)) ||
      Math.min(intrinsicHeight, COMPACT_PANEL_INNER_MAX_HEIGHT)
    );
  }, [
    hasAdvancedAiSearchItem,
    isMobile,
    itemsProp.length,
    isShowingLoadingMore,
    NoResults,
  ]);

  return (
    <div className={styles.compactShell}>
      <div className={styles.compactInputRow}>
        <ListSearch
          ref={inputRef}
          adornmentBefore={
            shouldShowProjectPill && (
              <ProjectScopeDropdown
                ref={dropdownRef}
                activeProjectId={activeProjectId as string}
                onClear={handleOnProjectPillClear}
                onFocusInput={focusInput}
                onSetProjectScope={handleOnSetProjectScope}
                scopedToProjectId={scopedToProjectId}
              />
            )
          }
          clearButtonAriaLabel={TEXT.clearButtonAriaLabel}
          data-testid="quick-search-compact-input"
          gapY="0"
          iconVariant="default"
          navigateBackLabel={TEXT.close}
          onKeyDown={handleSearchInputKeyDown}
          onNavigateBack={isMobile ? onRequestClose : undefined}
          onQueryClear={handleOnQueryClear}
          paddingBlockStart={0}
          paddingX={0}
          pendingStateLabel={TEXT.pendingStateLabel}
          placeholder={placeholderText}
          separator={null}
          size="large"
          state={listState}
          variant="pill"
        />
      </div>

      <div className={styles.compactResultsPanel} data-mobile={isMobile}>
        {hasSearchQuery ? (
          <div className={styles.searchResultsContainer}>
            {hasLoadingError ? (
              <div className={styles.noResultsContainer}>
                <ResultsMessage isError={true} onPress={handleOnClearAll} />
              </div>
            ) : (
              <div
                className={styles.listContentArea}
                style={{ height: resultsContentHeight }}
              >
                <div
                  className={clsx(
                    styles.listBoxScrollArea,
                    hasAdvancedAiSearchItem && styles.animateHeight
                  )}
                  style={
                    NoResults && !isShowingLoadingMore
                      ? {
                          height: hasAdvancedAiSearchItem
                            ? NO_RESULTS_SCROLL_AREA_HEIGHT_WITH_AI
                            : NO_RESULTS_SCROLL_AREA_HEIGHT,
                        }
                      : undefined
                  }
                >
                  <ListBox
                    {...(listProps as ListProps<QuickSearchResult>)}
                    ref={resultsListRef}
                    data-testid="quick-search-compact-results"
                    id={listState.id}
                    loading={listBoxLoadingState}
                    onScroll={handleOnListScroll}
                    paddingY={LIST_RESULTS_PADDING_Y}
                    renderLoading={renderListBoxLoading}
                    scrollPadding={LIST_SCROLL_PADDING}
                    scrollToKey={scrollToKey}
                    state={listState}
                    variant="unstyled"
                  />
                </div>
                {/* Suppress the empty-state message while the loading
                    skeleton is showing so the two states never overlap. */}
                {!isShowingLoadingMore && NoResults}
              </div>
            )}
          </div>
        ) : (
          <QuickSearchInitial
            id={listState.id}
            isCompact={true}
            onClickSearchSuggestion={handleOnClickSearchSuggestion}
            onScroll={handleOnInitialScroll}
            paddingTop={LIST_RESULTS_PADDING_Y}
            suggestionsCarouselProps={{
              cardWidth: SUGGESTION_CARD_WIDTH,
              cardsPerSubset: SUGGESTION_CARDS_PER_SUBSET,
            }}
          />
        )}
      </div>
    </div>
  );
}
